import type { ChooseUserEntryDataObject } from '@customerio/hydra-authentication';
import { isEmptyString } from '@/utils/string';

const GOOGLE_OIDC = 'google_oidc';

export type FormattedChooseUserEntry = ChooseUserEntryDataObject & {
  /** Blocked because the account's Google SSO hosted domain doesn't match the user's email domain. */
  hasGoogleSSODomainMismatch: boolean;
};

export function isOtherThanGoogleSso(provider: string): boolean {
  return !isEmptyString(provider) && provider !== GOOGLE_OIDC;
}

function hasDomainMismatch(entry: ChooseUserEntryDataObject): boolean {
  if (isEmptyString(entry.accountSSOOIDCGoogleHostedDomain)) {
    return false;
  }
  const identityEmail = !isEmptyString(entry.userExternalIdentityEmail)
    ? entry.userExternalIdentityEmail
    : entry.userEmail;
  // Domains are case-insensitive — compare lowercased.
  return (identityEmail.split('@')[1] ?? '').toLowerCase() !== entry.accountSSOOIDCGoogleHostedDomain.toLowerCase();
}

export function formatChooseUserEntries(entries: ChooseUserEntryDataObject[]): FormattedChooseUserEntry[] {
  return entries.map((entry) => ({ ...entry, hasGoogleSSODomainMismatch: hasDomainMismatch(entry) }));
}

/**
 * Accessible accounts first, then alphabetic by email — but un-aliased emails
 * (no `+`) sort before their aliases. Ported from the Ember choose-user sort.
 */
export function sortChooseUserEntries(entries: FormattedChooseUserEntry[]): FormattedChooseUserEntry[] {
  return [...entries].sort((a, b) => {
    if (a.hasGoogleSSODomainMismatch !== b.hasGoogleSSODomainMismatch) {
      return a.hasGoogleSSODomainMismatch ? 1 : -1;
    }
    // '+' sorts before '@', so replace it with '`' (which sorts after '@') to
    // push aliased emails below their un-aliased counterpart.
    const emailA = a.userEmail.replace(/\+/g, '`').toLowerCase();
    const emailB = b.userEmail.replace(/\+/g, '`').toLowerCase();
    if (emailA < emailB) return -1;
    if (emailA > emailB) return 1;
    return 0;
  });
}
